import { type FC, useMemo } from 'react';

import { Input } from '@dynamika-shared/ui/Input/Input';
import { InputNumber } from '@dynamika-shared/ui/InputNumber/InputNumber';
import { Select } from '@dynamika-shared/ui/Select/Select';
import { Switch } from '@dynamika-shared/ui/Switch/Switch';

import { useInventoryFormHandler } from '@hooks/useInventoryFormHandler';
import { useClinicalTrialInventorySlice } from '@restore/clinicalTrialInventory/clinicalTrialInventory.slice';
import { CodeEditor } from 'src/pages/ClinicalTrial/Readings/ReadingInventoryItemForm/CodeEditor';

export const Measurement: FC = () => {
  const { onChange, watch, errors } = useInventoryFormHandler();

  const [scoringForms] = useClinicalTrialInventorySlice((state, actions) => [
    state.scoringForms,
    actions,
  ]);
  const readersOptions = useMemo(() => {
    return scoringForms.map(scoringForm => ({
      label: scoringForm.name,
      value: scoringForm.id,
    }));
  }, [scoringForms]);

  return (
    <div className={'w-full grow col-span-2 gap-4'}>
      <div className={'flex justify-between'}>
        <div className={'grow min-w-[25%] me-4'}>
          <Select
            label={'Scoring Form'}
            placeholder={'Scoring Form'}
            listHeight={160}
            options={readersOptions}
            onChange={value => onChange('scoringFormId', value)}
            value={watch('scoringFormId')}
            data-testid='item-measurement'
          />
        </div>
        <InputNumber
          label={'Min Required:'}
          className={'w-7'}
          value={watch('minRequired')}
          onChange={value => onChange('minRequired', value as string)}
          schema={{ minimum: 0, maximum: 999 }}
          controls
        />
        <InputNumber
          label={'Max Allowance:'}
          className={'w-7'}
          value={watch('maxAllowance')}
          onChange={value => onChange('maxAllowance', value as string)}
          schema={{ minimum: 0, maximum: 999 }}
          controls
        />
        <Switch
          label={'Screenshot Required'}
          name={'Screenshot Required'}
          checked={watch('screenshotRequired')}
          onChange={e => onChange('screenshotRequired', e)}
          data-testid={'item-screenshot-required'}
          className={'mt-2.5'}
        />
        <Switch
          label={'Allow NA screenshot'}
          name={'Allow NA screenshot'}
          checked={watch('allowNaScreenshot')}
          onChange={e => onChange('allowNaScreenshot', e)}
          data-testid={'item-allow-na-screenshot'}
          className={'mt-2.5'}
        />
        <Input
          required
          value={watch('namePattern')}
          name={'namePattern'}
          onChange={e => onChange('namePattern', e.target.value)}
          data-testid={'item-namePattern'}
          errors={errors.name?.message as string}
          label={'Name pattern'}
          placeholder={'Name pattern'}
          classNameInputWrapper='max-w-full'
          maxLength={255}
        />
      </div>
      <div className={'mt-4 grid grid-cols-3 gap-4'}>
        <CodeEditor
          classNameWrapper={'min-w-0'}
          title={'Metadata'}
          language={'json'}
          value={watch('metadata')}
          onChange={value => onChange('metadata', value)}
        />
        <CodeEditor
          classNameWrapper={'min-w-0'}
          title={'Permanent Data Schema'}
          language={'json'}
          value={watch('permanentDataSchema')}
          onChange={value => onChange('permanentDataSchema', value)}
        />
        <CodeEditor
          classNameWrapper={'min-w-0'}
          title={'Measurement Data Schema'}
          language={'json'}
          value={watch('validationSchema')}
          onChange={value => onChange('validationSchema', value)}
        />
      </div>
    </div>
  );
};
